import { cva, cx } from '@vizit/theme';
import { Link, type LinkProps } from 'react-aria-components';
import { LinkSize, LinkType } from './types';

interface VizitLinkProps extends LinkProps {
  /**
   * The type variant of the link.
   *
   * @optional
   * @type 'external' | 'default'
   * @default 'default'
   */
  type?: LinkType;
  /**
   * The size of the text.
   *
   * @optional
   * @type LinkSize
   * @default LinkSize.Md
   */
  size?: LinkSize;
  /**
   * Whether the link represents the current/active route.
   *
   * @optional
   * @type boolean
   * @default false
   */
  isActive?: boolean;
}

const classes = cva({
  base: [
    'cursor-pointer outline-none relative',

    /* Disabled state */
    'disabled:opacity-50 disabled:pointer-events-none',

    /* Focus state */
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
  ],
  variants: {
    type: {
      external: [
        'underline text-brand-secondary font-semibold',
        /* Hover state */
        'hover:text-brand-secondary-hover',
      ],
      default: [
        'text-primary underline',
        /* Hover state */
        'hover:text-primary-hover',
        /* Focus state */
        'focus-visible:text-secondary-hover',
      ],
    },
    size: {
      sm: 'text-sm',
      md: 'text-md',
      lg: 'text-lg',
    },
    isActive: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [],
  defaultVariants: {
    type: 'default',
    size: 'md',
    isActive: false,
  },
});

/**
 * The Link component is used to create a link.
 *
 * @param props - The props for the Link component.
 * @example
 * <Link href="https://www.google.com">
 *   Link Text
 * </Link>
 */
function _Link(props: VizitLinkProps) {
  const { className, type = LinkType.Default, size = LinkSize.Md, isActive = false, ...other } = props;
  return <Link className={cx(classes({ type, size, isActive }), className)} {...other} />;
}

export type { VizitLinkProps as LinkProps };
export { _Link as Link };
